import type { FC } from "react";

import isString from "lodash/isString";
import type { FallbackProps } from "react-error-boundary";
import { ErrorBoundary } from "react-error-boundary";

import { PixelPlaceholderLoader } from "@similarweb/ui-components";

import { WidgetErrorFallback } from "app/components/error-boundary-no-data/ErrorBoundaryEmptyWidget";
import { EErrorTypes, GenericEmptyState } from "app/components/React/empty-state/GenericEmptyState";
import {
    StyledBox,
    LoaderWrapper,
    BodyWrapper,
    WidgetWrapper,
} from "app/components/React/WidgetContainer/styledComponents";
import { WidgetFooter } from "app/components/React/WidgetContainer/WidgetFooter";
import { WidgetHeader } from "app/components/React/WidgetContainer/WidgetHeader";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

import type { IWidgetContainerProps } from "./types";

const FallbackComponent = (props: FallbackProps) => <WidgetErrorFallback {...props} />;

export const WidgetContainer: FC<IWidgetContainerProps> = ({
    title,
    headerHeight,
    actionsTitle,
    tooltip,
    subtitleFilters = [],
    isLoading = false,
    showLoadingState = true,
    showSubtitleFiltersLoadingState = true,
    areSubtitleFiltersLoading = false,
    hideExcelButton,
    withBorder,
    minWidth,
    maxWidth,
    minHeight,
    bodyMinHeight,
    bodyHeight,
    height,
    wrapperHeight,
    footerWrapperComponent,
    loaderHeight = 24,
    loaderWidth = 304,
    excelUrl,
    onExcelClick,
    isExcelDisabled,
    onDownloadClick,
    downloadProps,
    noData = false,
    noDataTitle = null,
    noDataSubtitle = null,
    noDataType = EErrorTypes.NO_DATA,
    showSubtitle = true,
    onOffSwitchConfig = [],
    checkboxConfig = [],
    dynamicButtonsConfig = [],
    switcherConfig,
    switcherViewMode,
    dynamicSwitcherConfig = [],
    dynamicDropdownConfig = [],
    addToDashboardConfig,
    columnPickerConfig,
    children,
    noticeSlot,
    weShareProps,
    footerButtonsConfig,
    LoaderComponent,
    className,
    requestBodyForExcel,
    automationId,
    hideHeader,
    headerBadge,
    headerTitleSuffix,
    descriptionText,
    totalCount,
    hasPaidExcel,
    closeButtonProps,
    errorComponentProps = { fallbackComponent: FallbackComponent, resetKeys: [] },
}) => {
    const translate = useTranslation();

    const getNoDataSubtitle = (): string => {
        if (showSubtitle) {
            return noDataSubtitle ?? translate("apps.general.emptystate.subtitle");
        }
        return "";
    };

    return (
        <StyledBox
            data-automation={automationId}
            className={className}
            minWidth={minWidth}
            minHeight={minHeight}
            maxWidth={maxWidth}
            height={height}
        >
            <ErrorBoundary
                FallbackComponent={errorComponentProps.fallbackComponent}
                resetKeys={errorComponentProps.resetKeys}
            >
                <WidgetWrapper $height={wrapperHeight}>
                    {!hideHeader ? (
                        <WidgetHeader
                            title={title}
                            actionsTitle={actionsTitle}
                            headerHeight={headerHeight}
                            isLoading={
                                isLoading || areSubtitleFiltersLoading ? showSubtitleFiltersLoadingState : undefined
                            }
                            withBorder={withBorder}
                            tooltip={tooltip}
                            subtitleFilters={subtitleFilters}
                            excelUrl={excelUrl}
                            onExcelClick={onExcelClick}
                            isExcelDisabled={isExcelDisabled}
                            onDownloadClick={onDownloadClick}
                            downloadProps={downloadProps}
                            onOffSwitchConfig={onOffSwitchConfig}
                            checkboxConfig={checkboxConfig}
                            dynamicButtonsConfig={dynamicButtonsConfig}
                            switcherConfig={switcherConfig}
                            switcherViewMode={switcherViewMode}
                            dynamicSwitcherConfig={dynamicSwitcherConfig}
                            dynamicDropdownConfig={dynamicDropdownConfig}
                            addToDashboardConfig={addToDashboardConfig}
                            columnPickerConfig={columnPickerConfig}
                            weShareProps={weShareProps}
                            noData={noData}
                            headerBadge={headerBadge}
                            headerTitleSuffix={headerTitleSuffix}
                            hideExcelButton={hideExcelButton}
                            requestBodyForExcel={requestBodyForExcel}
                            descriptionText={descriptionText}
                            totalCount={totalCount}
                            hasPaidExcel={hasPaidExcel}
                            closeButtonProps={closeButtonProps}
                        />
                    ) : null}
                    {noticeSlot}
                    <BodyWrapper
                        // @ts-ignore FIXME: strict migration - Type 'number | undefined' is not assignable to type 'number' — bodyMinHeight is optional in props but required in styled component
                        bodyMinHeight={bodyMinHeight}
                        bodyHeight={bodyHeight}
                        noData={noData}
                        hasFooter={!!footerButtonsConfig}
                    >
                        {showLoadingState && isLoading ? (
                            <LoaderWrapper
                                usePercentageWidth={
                                    isString(loaderWidth) && loaderWidth.includes("%") ? true : undefined
                                }
                            >
                                {LoaderComponent ?? (
                                    <PixelPlaceholderLoader
                                        key={"element-loader"}
                                        width={loaderWidth}
                                        height={loaderHeight}
                                    />
                                )}
                            </LoaderWrapper>
                        ) : noData ? (
                            <GenericEmptyState
                                isFullPage={false}
                                title={noDataTitle ?? translate("common.noresults")}
                                subtitle={getNoDataSubtitle()}
                                type={noDataType}
                            />
                        ) : (
                            children
                        )}
                    </BodyWrapper>
                    {footerButtonsConfig ? (
                        <WidgetFooter
                            wrapperComponent={footerWrapperComponent}
                            buttonsConfig={footerButtonsConfig}
                        />
                    ) : null}
                </WidgetWrapper>
            </ErrorBoundary>
        </StyledBox>
    );
};
